import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {
  RecordAuditDefinitionSection,
  RecordAuditDefinitionTask,
} from '../types/RecordAuditDefinitionFormValue';
import {orderTasksForDisplay} from './taskListOperations';

// The whole editor is rendered as ONE flat, sortable list. A "section" is just a
// header row inside that list; a task belongs to whichever header sits above it.
// Section membership is therefore derived from position on drop — there is no
// cross-container drag to special-case. The first header ("Tasks without
// section") is fixed: it cannot be dragged or deleted.

export interface AuditHeaderRow {
  id: string;
  kind: 'header';
  sectionId: string | null;
  name: string;
  isFixed: boolean;
}

export interface AuditTaskRow {
  id: string;
  kind: 'task';
  flatIndex: number;
  task: RecordAuditDefinitionTask;
}

export type AuditRow = AuditHeaderRow | AuditTaskRow;

// `generation` is bumped on every structural mutation so row ids (and therefore
// the keyed task inputs) remount and re-read fresh values from the field array.
export const buildAuditRows = (
  tasks: RecordAuditDefinitionTask[],
  sections: RecordAuditDefinitionSection[],
  generation: number
): AuditRow[] => {
  const indexed = tasks.map((task, flatIndex) => ({task, flatIndex}));
  const taskRowsFor = (sectionId: string | null): AuditTaskRow[] =>
    indexed
      .filter(({task}) => task.sectionId === sectionId)
      .map(({task, flatIndex}) => ({
        id: `g${generation}-task-${flatIndex}`,
        kind: 'task',
        flatIndex,
        task,
      }));

  const rows: AuditRow[] = [
    {
      id: `g${generation}-header-none`,
      kind: 'header',
      sectionId: null,
      name: i18n.t('page.settings.recordAuditDefinition.unsectionedTasks'),
      isFixed: true,
    },
    ...taskRowsFor(null),
  ];

  sections.forEach((section) => {
    rows.push({
      id: `g${generation}-header-${section.id}`,
      kind: 'header',
      sectionId: section.id,
      name: section.name,
      isFixed: false,
    });
    rows.push(...taskRowsFor(section.id));
  });

  return rows;
};

// Moving a header carries its block of tasks; moving a task moves just that task.
export const moveAuditRows = (rows: AuditRow[], fromIndex: number, toIndex: number): AuditRow[] => {
  const active = rows[fromIndex];
  const target = rows[toIndex];
  if (isNotNil(active) === false || isNotNil(target) === false) {
    return rows;
  }

  let blockLength = 1;
  if (active.kind === 'header') {
    while (rows[fromIndex + blockLength]?.kind === 'task') {
      blockLength += 1;
    }
  }

  const block = rows.slice(fromIndex, fromIndex + blockLength);
  if (block.includes(target)) {
    return rows;
  }

  const rest = rows.filter((row) => block.includes(row) === false);
  const targetIndex = rest.indexOf(target);
  // Dragging downward lands the block AFTER the target row; dragging upward, before
  // it. Without the +1 a downward drop stops one row short of the cursor.
  const insertionIndex = fromIndex < toIndex ? targetIndex + 1 : targetIndex;
  return [...rest.slice(0, insertionIndex), ...block, ...rest.slice(insertionIndex)];
};

// Read the section order and each task's section straight off the row order.
export const auditRowsToFormValue = (
  rows: AuditRow[]
): {sections: RecordAuditDefinitionSection[]; tasks: RecordAuditDefinitionTask[]} => {
  const sections: RecordAuditDefinitionSection[] = rows
    .filter((row): row is AuditHeaderRow => row.kind === 'header' && isNotNil(row.sectionId))
    .map((row) => ({id: row.sectionId ?? '', name: row.name}));

  let currentSectionId: string | null = null;
  const tasks: RecordAuditDefinitionTask[] = [];
  rows.forEach((row) => {
    if (row.kind === 'header') {
      currentSectionId = row.sectionId;
      return;
    }
    tasks.push({...row.task, sectionId: currentSectionId});
  });

  return {sections, tasks: orderTasksForDisplay(tasks, sections)};
};

// The section a row belongs to: the nearest header at or above `index`
// (null = the fixed unsectioned block).
export const sectionIdAtRow = (rows: AuditRow[], index: number): string | null => {
  for (let cursor = Math.min(index, rows.length - 1); cursor >= 0; cursor -= 1) {
    const row = rows[cursor];
    if (row?.kind === 'header') {
      return row.sectionId;
    }
  }
  return null;
};

// Section drags reorder whole sections (never individual rows), so a section can
// only ever land on a section boundary — tasks of other sections can never be
// absorbed. Dropping onto the unsectioned block moves the section to the front.
export const reorderSections = (
  sections: RecordAuditDefinitionSection[],
  tasks: RecordAuditDefinitionTask[],
  fromSectionId: string,
  toSectionId: string | null
): {sections: RecordAuditDefinitionSection[]; tasks: RecordAuditDefinitionTask[]} => {
  const fromIndex = sections.findIndex((section) => section.id === fromSectionId);
  if (fromIndex < 0) {
    return {sections, tasks};
  }
  const targetIndex = isNotNil(toSectionId)
    ? sections.findIndex((section) => section.id === toSectionId)
    : 0;
  if (targetIndex < 0 || targetIndex === fromIndex) {
    return {sections, tasks};
  }

  const nextSections = [...sections];
  const [moved] = nextSections.splice(fromIndex, 1);
  nextSections.splice(targetIndex, 0, moved);

  return {sections: nextSections, tasks: orderTasksForDisplay(tasks, nextSections)};
};
